/**
 * Phase 36 Batch AF — Effective Price Banner.
 *
 * Renders above price-dependent surfaces (Financials, Scenarios,
 * Projection, Report) explaining which price is currently driving the
 * analysis: original wizard purchase_price, the user's counter-offer,
 * or the AI's recommended offer.
 *
 * Click "Change basis" → opens an inline dropdown that PATCHes
 * /deals/{id}/target-offer and bubbles the refreshed deal back up via
 * onDealUpdated.
 *
 * Visual policy:
 *   * Hidden when basis is missing OR equal to "purchase_price" AND no
 *     prior override has been logged (a brand-new deal with no
 *     counter — no need to show the strip).
 *   * Amber accent for active counter-offers (draws the eye to the
 *     fact that the financials show "your price, not the listing").
 *   * Indigo when the basis is "ai_recommended_offer".
 *   * Subtle slate when restored to "purchase_price" with a prior
 *     history (so the user still has a one-click switch back).
 */
import { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useFmt } from "@/lib/useFmt";
import { Banknote, Sparkles, Tag, ChevronDown, Check, Loader2 } from "lucide-react";


const BASIS_OPTIONS = [
  {
    id: "purchase_price",
    label: "Original purchase price",
    icon: Tag,
    needsAmount: false,
    description: "Use the price you entered in the wizard.",
  },
  {
    id: "user_target_offer",
    label: "My counter-offer",
    icon: Banknote,
    needsAmount: true,
    description: "Type the price you plan to offer.",
  },
  {
    id: "ai_recommended_offer",
    label: "AI recommended offer",
    icon: Sparkles,
    needsAmount: false,
    description: "Apply the latest AI Negotiation recommendation.",
  },
];


function basisVisuals(basis) {
  switch (basis) {
    case "user_target_offer":
      return {
        wrapper: "border-amber-200 bg-amber-50",
        icon: Banknote,
        iconCls: "bg-amber-600 text-white",
        eyebrow: "text-amber-700",
      };
    case "ai_recommended_offer":
      return {
        wrapper: "border-indigo-200 bg-indigo-50",
        icon: Sparkles,
        iconCls: "bg-indigo-600 text-white",
        eyebrow: "text-indigo-700",
      };
    default:
      return {
        wrapper: "border-slate-200 bg-slate-50",
        icon: Tag,
        iconCls: "bg-slate-600 text-white",
        eyebrow: "text-slate-700",
      };
  }
}


export default function EffectivePriceBanner({ deal, onDealUpdated }) {
  const fmt = useFmt();
  const [open, setOpen] = useState(false);
  const [pendingBasis, setPendingBasis] = useState(null);
  const [amountInput, setAmountInput] = useState("");
  const [saving, setSaving] = useState(false);

  const basis = deal?.effective_purchase_price_basis || "purchase_price";
  const effective = Number(deal?.purchase_price) || 0;
  const original = Number(deal?.original_purchase_price);
  const userTarget = Number(deal?.user_target_offer);
  const history = deal?.counter_offer_history || [];
  const hasHistory = history.length > 0;

  // Don't render at all on brand-new deals where the user hasn't
  // touched price yet — that's the default state and we don't want to
  // make the chrome busy.
  const shouldRender = useMemo(() => {
    if (basis !== "purchase_price") return true;
    return hasHistory;
  }, [basis, hasHistory]);

  if (!shouldRender) return null;

  const vis = basisVisuals(basis);
  const Icon = vis.icon;
  const activeOption = BASIS_OPTIONS.find((o) => o.id === basis) || BASIS_OPTIONS[0];

  // Derived display strings.
  const eyebrowText = (() => {
    if (basis === "user_target_offer") return "Counter-offer applied";
    if (basis === "ai_recommended_offer") return "AI recommended offer applied";
    return "Original price";
  })();
  const explainerSuffix = (() => {
    // When user is on a non-original basis, show what the original was
    // so they remember the delta.
    if (basis === "purchase_price") return "";
    if (!Number.isFinite(original) || original <= 0) return "";
    const delta = effective - original;
    const pct = ((delta / original) * 100).toFixed(1);
    const sign = delta > 0 ? "+" : "";
    return ` · original list ${fmt.money(original)} (${sign}${pct}%)`;
  })();

  const beginChange = (option) => {
    setPendingBasis(option.id);
    if (option.id === "user_target_offer") {
      setAmountInput(userTarget > 0 ? String(Math.round(userTarget)) : String(Math.round(effective)));
    } else {
      setAmountInput("");
    }
  };

  const cancelChange = () => {
    setPendingBasis(null);
    setAmountInput("");
  };

  const applyChange = async () => {
    if (!pendingBasis) return;
    const payload = { basis: pendingBasis };
    if (pendingBasis === "user_target_offer") {
      const amt = Number(amountInput);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error("Enter a positive offer amount.");
        return;
      }
      payload.amount = amt;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/deals/${deal.id}/target-offer`,
        payload,
      );
      onDealUpdated?.(data);
      try {
        const fromBasis = basis;
        const toBasis = pendingBasis;
        window.posthog?.capture?.("effective_price_basis_changed", {
          deal_id: deal.id,
          from_basis: fromBasis,
          to_basis: toBasis,
          amount_delta: (Number(data?.purchase_price) || 0) - effective,
        });
        if (toBasis === "user_target_offer") {
          window.posthog?.capture?.("counter_offer_persisted", {
            deal_id: deal.id,
            amount: payload.amount,
            source: "effective_price_banner",
          });
        }
      } catch {
        /* posthog blocked — ignore */
      }
      toast.success(
        pendingBasis === "user_target_offer"
          ? `Counter-offer ${fmt.money(Number(payload.amount))} applied to financials`
          : pendingBasis === "ai_recommended_offer"
          ? "AI recommended offer applied to financials"
          : "Restored to original purchase price"
      );
      setPendingBasis(null);
      setAmountInput("");
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not apply.";
      toast.error(typeof msg === "string" ? msg : "Could not apply.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="effective-price-banner"
      data-basis={basis}
      className={`mb-4 rounded-2xl border p-4 shadow-sm sm:p-5 ${vis.wrapper}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${vis.iconCls}`}>
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className={`text-[10px] font-semibold uppercase tracking-wider ${vis.eyebrow}`}>
              {eyebrowText}
            </div>
            <p
              data-testid="effective-price-headline"
              className="text-sm font-semibold text-slate-900 sm:text-base"
            >
              Financials based on {fmt.money(effective)}
              <span className="font-normal text-slate-600">{explainerSuffix}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="effective-price-change-btn"
          className="inline-flex flex-none items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          Change basis <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div
          data-testid="effective-price-panel"
          className="mt-4 grid gap-2 border-t border-slate-200/70 pt-4 sm:grid-cols-3"
        >
          {BASIS_OPTIONS.map((opt) => {
            const Active = basis === opt.id;
            const Pending = pendingBasis === opt.id;
            const OptIcon = opt.icon;
            return (
              <div
                key={opt.id}
                data-testid={`effective-price-opt-${opt.id}`}
                className={`flex flex-col rounded-lg border p-3 transition-colors ${
                  Pending
                    ? "border-indigo-300 bg-white ring-2 ring-indigo-100"
                    : Active
                    ? "border-slate-300 bg-white"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <OptIcon className="h-3.5 w-3.5 text-slate-600" />
                  <span className="text-xs font-semibold text-slate-900">{opt.label}</span>
                  {Active && !Pending && (
                    <span className="ml-auto text-[10px] font-medium uppercase tracking-wider text-emerald-700">
                      <Check className="inline h-3 w-3" /> active
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">{opt.description}</p>
                {Pending && opt.needsAmount ? (
                  <input
                    type="number"
                    inputMode="numeric"
                    value={amountInput}
                    onChange={(e) => setAmountInput(e.target.value)}
                    placeholder="e.g. 280000"
                    data-testid={`effective-price-amount-${opt.id}`}
                    className="mt-2 w-full rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
                    autoFocus
                  />
                ) : null}
                <div className="mt-2 flex justify-end gap-2">
                  {Pending ? (
                    <>
                      <button
                        type="button"
                        onClick={cancelChange}
                        className="text-xs font-medium text-slate-500 hover:text-slate-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={applyChange}
                        data-testid={`effective-price-apply-${opt.id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        Apply
                      </button>
                    </>
                  ) : Active ? (
                    <span className="text-[10px] text-slate-400">in use</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => beginChange(opt)}
                      data-testid={`effective-price-pick-${opt.id}`}
                      className="text-xs font-medium text-indigo-700 hover:underline"
                    >
                      Use this
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
